<script setup lang="ts">
/**
 * ViewCommandBar
 *
 * Main toolbar for saved views — entity-agnostic.
 * Replaces the old filter toggle in each list view.
 *
 * Orchestrates: ViewSwitcherDropdown, FilterFieldPicker,
 * FilterBuilderPanel, SaveViewModal, ViewActionsMenu, StaticItemsPanel.
 */

import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useUiStore } from '@/stores/ui'
import { ConfirmModal } from '@/components'
import ViewSwitcherDropdown from './ViewSwitcherDropdown.vue'
import FilterFieldPicker from './FilterFieldPicker.vue'
import FilterBuilderPanel from './FilterBuilderPanel.vue'
import SaveViewModal from './SaveViewModal.vue'
import ViewActionsMenu from './ViewActionsMenu.vue'
import QuickFilterPanel from './QuickFilterPanel.vue'


import type {
    SavedViewEntityType,
    FilterGroup,
    FilterOperator,
    SortConfig,
    SavedView,
    SavedViewVisibility,
} from '../api/types'
import { useSavedViews } from '../composables/useSavedViews'
import { getFilterSummary } from '../composables/filterAdapter'
import { getQuickFilterConfigs } from '../composables/quickFilterConfigs'
import { useCSVExport, type ExportColumn } from '../composables/useCSVExport'
import { isFilterCondition } from '../api/types'

interface Props {
    entityType: SavedViewEntityType
    /** Current filter state from the parent list view */
    currentFilters?: FilterGroup
    currentSort?: SortConfig
    resultCount?: number
    /** Column definitions for CSV export. If provided, shows the Export button. */
    exportColumns?: ExportColumn[]
}

const props = defineProps<Props>()

const emit = defineEmits<{
    /** Emitted when a view is selected or filters change — parent applies these */
    'apply-filters': [filters: FilterGroup | undefined]
    'apply-sort': [sort: SortConfig | undefined]
    'clear-filters': []
    /** Emitted when the active view changes (or is cleared) */
    'view-changed': [view: SavedView | null]
}>()

const authStore = useAuthStore()
const uiStore = useUiStore()
const route = useRoute()
const router = useRouter()

const {
    views,
    activeView,
    fields,
    error,
    canEdit,
    myViews,
    sharedViews,
    systemViews,
    hasActiveView,
    loadSchema,
    loadViews,
    selectView,
    clearView,
    saveCurrentFilters,
    updateView,
    deleteView,
    duplicateView,
    freezeView,
    unfreezeView,
    checkUnsavedChanges,
    restoreRecentView,
    setDefaultView,
    resetDefaultView,
} = useSavedViews(props.entityType)

// ─── UI State ───────────────────────────────────────────────────

const showViewSwitcher = ref(false)
const showFilterPicker = ref(false)
const showAdvancedBuilder = ref(false)
const showSaveModal = ref(false)
const showActionsMenu = ref(false)
const actionsMenuView = ref<SavedView | null>(null)
const viewCounts = ref(new Map<string, number>())
const showDeleteConfirm = ref(false)
const deleteTarget = ref<SavedView | null>(null)
const renamingViewId = ref<string | null>(null)

// ─── CSV Export ─────────────────────────────────────────────────────

const { isExporting, exportProgress, exportAll } = useCSVExport()

const canExport = computed(() =>
    props.exportColumns && props.exportColumns.length > 0
)

const hasExportableResults = computed(() => (props.resultCount ?? 0) > 0)
const renameValue = ref('')

// ─── Quick Filter Panel ─────────────────────────────────────────

const quickFilterConfigs = getQuickFilterConfigs(props.entityType)
const hasQuickFilters = quickFilterConfigs.length > 0

const storageKey = `quickFilterExpanded:${props.entityType}`
const isQuickPanelExpanded = ref(
    hasQuickFilters && localStorage.getItem(storageKey) === 'true'
)

function toggleQuickPanel() {
    isQuickPanelExpanded.value = !isQuickPanelExpanded.value
    localStorage.setItem(storageKey, String(isQuickPanelExpanded.value))
    if (isQuickPanelExpanded.value) {
        showAdvancedBuilder.value = false
    }
}

function onQuickFilterUpdate(field: string, op: FilterOperator, value: unknown) {
    const currentConditions = (props.currentFilters?.conditions ?? []).filter(c => {
        if (!isFilterCondition(c)) return true
        return !(c.field === field && c.op === op)
    })
    const newFilters: FilterGroup = {
        operator: props.currentFilters?.operator ?? 'AND',
        conditions: [...currentConditions, { field, op, value }],
    }
    emit('apply-filters', newFilters)
}

function onQuickFilterRemove(field: string, op: FilterOperator) {
    if (!props.currentFilters) return
    const newConditions = props.currentFilters.conditions.filter(c => {
        if (!isFilterCondition(c)) return true
        return !(c.field === field && c.op === op)
    })
    if (newConditions.length === 0 && !hasActiveView.value) {
        emit('clear-filters')
    } else {
        emit('apply-filters', {
            operator: props.currentFilters.operator,
            conditions: newConditions,
        })
    }

}

function onQuickClearAll() {
    emit('clear-filters')
}

function onQuickOpenAdvanced() {
    isQuickPanelExpanded.value = false
    showAdvancedBuilder.value = true
}

// ─── Computed ───────────────────────────────────────────────────

const activeViewName = computed(() =>
    activeView.value?.name ?? `All ${entityLabel.value}`
)

const entityLabel = computed(() => {
    const labels: Record<SavedViewEntityType, string> = {
        property: 'Properties',
        tenant: 'Tenants',
        contact: 'Contacts',
        lead: 'Leads',
        weather_event: 'Weather Events',
        project: 'Projects',
        weather_report: 'Weather Reports',
        weather_report_lead: 'Weather Report Leads',
        fleet_machine: 'Machines',
        fleet_app_classification: 'App Classifications',
        fleet_domain_classification: 'Domain Classifications',
        fleet_daily_summary: 'Daily Summaries',
        contract_review: 'Contract Reviews',
        optimizer_job: 'Optimizer Jobs',
        optimizer_material_preset: 'Material Presets',
        it_request: 'IT Requests',
        fab_job: 'Fab Jobs',
    }
    return labels[props.entityType]
})

const filterPills = computed(() => {
    if (!props.currentFilters || props.currentFilters.conditions.length === 0) return []
    return getFilterSummary(props.currentFilters)
})

const hasFilters = computed(() => filterPills.value.length > 0)

const hasUnsavedChanges = computed(() =>
    checkUnsavedChanges(props.currentFilters, props.currentSort)
)



// ─── Event Handlers ─────────────────────────────────────────────

async function onSelectView(viewId: string) {
    showViewSwitcher.value = false
    const result = await selectView(viewId)
    if (result) {
        emit('apply-filters', result.filters)
        emit('apply-sort', result.sort)
    }
    emit('view-changed', activeView.value)
    updateViewQueryParam(activeView.value?.id ?? null)
}

function onClearView() {
    showViewSwitcher.value = false
    clearView()
    emit('clear-filters')
    emit('view-changed', null)
    updateViewQueryParam(null)
}

/** Update the ?view= query param without triggering navigation */
function updateViewQueryParam(viewId: string | null) {
    const query = { ...route.query }
    if (viewId) {
        query.view = viewId
    } else {
        delete query.view
    }
    router.replace({ query })
}

function onAddCondition(field: string, op: FilterOperator, value: unknown) {
    showFilterPicker.value = false

    // Build a new filter group with the added condition
    const currentConditions = props.currentFilters?.conditions ?? []
    const newFilters: FilterGroup = {
        operator: props.currentFilters?.operator ?? 'AND',
        conditions: [...currentConditions, { field, op, value }],
    }
    emit('apply-filters', newFilters)
}

function onRemovePill(index: number) {
    if (!props.currentFilters) return

    const newConditions = [...props.currentFilters.conditions]
    newConditions.splice(index, 1)

    if (newConditions.length === 0 && !hasActiveView.value) {
        // No view active — fully clear
        emit('clear-filters')
    } else {
        // View active or still has conditions — emit updated group
        emit('apply-filters', {
            operator: props.currentFilters.operator,
            conditions: newConditions,
        })
    }
}

function onAdvancedApply(filters: FilterGroup) {
    showAdvancedBuilder.value = false
    emit('apply-filters', filters)
}

async function onSave(name: string, visibility: SavedViewVisibility) {
    if (!props.currentFilters || props.currentFilters.conditions.length === 0) {
        uiStore.error('Add at least one filter before saving a view')
        return
    }
    await saveCurrentFilters(name, props.currentFilters, props.currentSort, visibility)
    showSaveModal.value = false
}

async function onSaveExisting() {
    if (!activeView.value) return
    const filters = props.currentFilters ?? undefined
    try {
        await updateView(activeView.value.id, {
            filters,
            sort: props.currentSort,
        })
    } catch (err) {
        console.error('Failed to save view:', err)
    }
}

function onDiscard() {
    if (!activeView.value) return
    emit('apply-filters', activeView.value.filters)
    emit('apply-sort', activeView.value.sort)
}

function onOpenActions(view: SavedView, _event: MouseEvent) {
    actionsMenuView.value = view
    showActionsMenu.value = true
}

function onRename(view: SavedView) {
    showActionsMenu.value = false
    renamingViewId.value = view.id
    renameValue.value = view.name
}

async function onRenameSubmit(viewId: string, newName: string) {
    renamingViewId.value = null
    if (newName.trim() && newName.trim() !== views.value.find(v => v.id === viewId)?.name) {
        await updateView(viewId, { name: newName.trim() })
    }
}

function onRenameCancel() {
    renamingViewId.value = null
}

async function onDuplicate(view: SavedView) {
    await duplicateView(view.id)
}

async function onShare(view: SavedView) {
    const newVisibility = view.visibility === 'shared' ? 'private' : 'shared'
    await updateView(view.id, { visibility: newVisibility })
}

async function onFreeze(view: SavedView) {
    await freezeView(view.id)
}

async function onUnfreeze(view: SavedView) {
    await unfreezeView(view.id)
}

function onDeleteView(view: SavedView) {
    deleteTarget.value = view
    showDeleteConfirm.value = true
}

async function onConfirmDelete() {
    if (deleteTarget.value) {
        await deleteView(deleteTarget.value.id)
    }
    showDeleteConfirm.value = false
    deleteTarget.value = null
}

async function onToggleDefault(view: SavedView) {
    if (view.isDefault) {
        await resetDefaultView()
    } else {
        await setDefaultView(view.id)
    }
}

async function onSetDefault(view: SavedView) {
    await setDefaultView(view.id)
}

async function onResetDefault() {
    await resetDefaultView()
}

function closeAllDropdowns() {
    showViewSwitcher.value = false
    showFilterPicker.value = false
    showActionsMenu.value = false
}

async function onExportCSV() {
    if (!props.exportColumns || isExporting.value) return

    try {
        await exportAll({
            columns: props.exportColumns,
            filename: entityLabel.value.toLowerCase().replace(/\s+/g, '-'),
            entityType: props.entityType,
            filters: props.currentFilters,
            sort: props.currentSort,
        })
        uiStore.success('Export complete')
    } catch (err) {
        console.error('CSV export failed:', err)
        uiStore.error('Export failed. Please try again.')
    }
}

// ─── Click outside handler ──────────────────────────────────────

const commandBarRef = ref<HTMLElement | null>(null)

function onClickOutside(event: MouseEvent) {
    const target = event.target as HTMLElement
    // Only close dropdowns if the click is inside the command bar but outside a dropdown area.
    // Clicks outside the command bar entirely are ignored — they belong to the page.
    if (commandBarRef.value?.contains(target) && !target.closest('.command-bar__dropdown-area')) {
        closeAllDropdowns()
    }
    // If click is completely outside the command bar, close all dropdowns too
    if (commandBarRef.value && !commandBarRef.value.contains(target)) {
        closeAllDropdowns()
    }
}

// ─── Lifecycle ──────────────────────────────────────────────────

onMounted(async () => {
    await Promise.all([loadSchema(), loadViews()])
    document.addEventListener('mousedown', onClickOutside)

    // Auto-select view from ?view= query param
    const viewId = route.query.view as string | undefined
    if (viewId) {
        await onSelectView(viewId)
    } else {
        // No explicit query param — attempt MRU restore from cache
        const restored = await restoreRecentView()
        if (restored) {
            emit('apply-filters', restored.filters)
            emit('apply-sort', restored.sort)
            emit('view-changed', activeView.value)
            updateViewQueryParam(activeView.value?.id ?? null)
        }
    }
})

onUnmounted(() => {
    document.removeEventListener('mousedown', onClickOutside)
})
</script>

<template>
    <div ref="commandBarRef" class="command-bar" data-testid="view-command-bar">
        <div class="command-bar__row">
            <!-- View Selector -->
            <div class="command-bar__view-selector command-bar__dropdown-area">
                <button
                    data-testid="view-selector-btn"
                    class="command-bar__view-btn"
                    @click.stop="showViewSwitcher = !showViewSwitcher; showFilterPicker = false"
                >
                    <span class="command-bar__view-name">{{ activeViewName }}</span>
                    <svg class="command-bar__chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M6 9l6 6 6-6" />
                    </svg>
                </button>

                <!-- Modified indicator -->
                <span v-if="hasUnsavedChanges" class="command-bar__modified-badge">Modified</span>

                <!-- View Switcher Dropdown -->
                <Transition name="dropdown">
                    <ViewSwitcherDropdown
                        v-if="showViewSwitcher"
                        :my-views="myViews"
                        :shared-views="sharedViews"
                        :system-views="systemViews"
                        :active-view-id="activeView?.id ?? null"
                        :counts="viewCounts"
                        :renaming-view-id="renamingViewId"
                        :rename-value="renameValue"
                        @select="onSelectView"
                        @clear="onClearView"
                        @create="showSaveModal = true; showViewSwitcher = false"
                        @open-actions="onOpenActions"
                        @rename-submit="onRenameSubmit"
                        @rename-cancel="onRenameCancel"
                        @update:rename-value="renameValue = $event"
                        @toggle-default="onToggleDefault"
                    />
                </Transition>

                <!-- Actions Menu -->
                <Transition name="dropdown">
                    <ViewActionsMenu
                        v-if="showActionsMenu && actionsMenuView"
                        :view="actionsMenuView"
                        :current-user-id="authStore.user?.id ?? ''"
                        :is-admin="authStore.isSuperAdmin"
                        @rename="onRename"
                        @duplicate="onDuplicate"
                        @share="onShare"
                        @freeze="onFreeze"
                        @unfreeze="onUnfreeze"
                        @delete="onDeleteView"
                        @set-default="onSetDefault"
                        @reset-default="onResetDefault"
                        @close="showActionsMenu = false"
                    />
                </Transition>
            </div>

            <!-- Filter Pills -->
            <div class="command-bar__pills">
                <button
                    v-for="(pill, index) in filterPills"
                    :key="index"
                    class="command-bar__pill"
                    data-testid="filter-chip"
                    @click="onRemovePill(index)"
                    :title="`Remove: ${pill}`"
                >
                    {{ pill }}
                    <span class="command-bar__pill-remove">×</span>
                </button>
            </div>

            <!-- Spacer -->
            <div class="command-bar__spacer" />

            <!-- Quick Panel Toggle -->
            <button
                v-if="hasQuickFilters"
                class="command-bar__toggle-btn"
                :class="{ 'command-bar__toggle-btn--expanded': isQuickPanelExpanded }"
                :title="isQuickPanelExpanded ? 'Collapse quick filters' : 'Expand quick filters'"
                @click="toggleQuickPanel"
            >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M6 9l6 6 6-6" />
                </svg>
            </button>

            <!-- Actions -->
            <div class="command-bar__actions">
                <!-- + Filter Button -->
                <div class="command-bar__dropdown-area" v-if="canEdit">
                    <button
                        data-testid="add-filter-btn"
                        class="command-bar__action-btn"
                        @click.stop="showFilterPicker = !showFilterPicker; showViewSwitcher = false"
                    >
                        + Filter
                    </button>

                    <Transition name="dropdown">
                        <FilterFieldPicker
                            v-if="showFilterPicker"
                            :fields="fields"
                            :entity-type="entityType"
                            @add-condition="onAddCondition"
                            @open-advanced="showAdvancedBuilder = true; showFilterPicker = false"
                            @close="showFilterPicker = false"
                        />
                    </Transition>
                </div>

                <!-- Edit Filters (Advanced) -->
                <button
                    v-if="canEdit && hasFilters"
                    class="command-bar__action-btn"
                    @click="showAdvancedBuilder = !showAdvancedBuilder"
                >
                    Edit Filters
                </button>

                <!-- Export CSV -->
                <button
                    v-if="canExport"
                    data-testid="export-csv-btn"
                    class="command-bar__action-btn command-bar__export-btn"
                    :disabled="isExporting || !hasExportableResults"
                    @click="onExportCSV"
                >
                    <svg v-if="!isExporting" class="command-bar__export-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
                        <polyline points="7 10 12 15 17 10" />
                        <line x1="12" y1="15" x2="12" y2="3" />
                    </svg>
                    <svg v-else class="command-bar__export-icon command-bar__export-icon--spinning" viewBox="0 0 24 24" fill="none">
                        <circle class="spinner__track" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="3" />
                        <path class="spinner__head" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    <span v-if="isExporting && exportProgress">
                        {{ exportProgress.current.toLocaleString() }}/{{ exportProgress.total.toLocaleString() }}
                    </span>
                    <span v-else>Export</span>
                </button>

                <!-- Result Count -->
                <span v-if="resultCount !== undefined" class="command-bar__count" data-testid="result-count">
                    {{ resultCount.toLocaleString() }} result{{ resultCount !== 1 ? 's' : '' }}
                </span>

                <!-- Save / Save As Buttons -->
                <template v-if="canEdit && hasUnsavedChanges && hasActiveView">
                    <button
                        class="command-bar__save-btn"
                        @click="onSaveExisting"
                    >
                        Save
                    </button>
                    <button
                        class="command-bar__save-btn command-bar__save-btn--secondary"
                        @click="showSaveModal = true"
                    >
                        Save as New
                    </button>
                    <button
                        class="command-bar__discard-btn"
                        @click="onDiscard"
                    >
                        Discard
                    </button>
                </template>
                <button
                    v-else-if="canEdit && hasFilters && !hasActiveView"
                    data-testid="save-view-btn"
                    class="command-bar__save-btn"
                    @click="showSaveModal = true"
                >
                    Save View
                </button>
            </div>
        </div>

        <!-- Quick Filter Panel -->
        <Transition name="slide">
            <QuickFilterPanel
                v-if="hasQuickFilters && isQuickPanelExpanded"
                :configs="quickFilterConfigs"
                :current-filters="currentFilters"
                :entity-type="entityType"
                :fields="fields"
                @update-filter="onQuickFilterUpdate"
                @remove-filter="onQuickFilterRemove"
                @open-advanced="onQuickOpenAdvanced"
                @clear-all="onQuickClearAll"
            />
        </Transition>

        <!-- Advanced Builder Panel -->
        <Transition name="slide">
            <FilterBuilderPanel
                v-if="showAdvancedBuilder"
                :entity-type="entityType"
                :fields="fields"
                :initial-filters="currentFilters"
                @apply="onAdvancedApply"
                @cancel="showAdvancedBuilder = false"
            />
        </Transition>




        <!-- Error Message -->
        <div v-if="error" class="command-bar__error">
            {{ error }}
        </div>

        <!-- Save View Modal -->
        <!-- Delete Confirmation Modal -->
        <ConfirmModal
            v-model="showDeleteConfirm"
            title="Delete View"
            :message="`Permanently delete \&quot;${deleteTarget?.name}\&quot;? This cannot be undone.`"
            confirm-label="Delete"
            variant="danger"
            @confirm="onConfirmDelete"
            @cancel="showDeleteConfirm = false; deleteTarget = null"
        />

        <SaveViewModal
            v-model="showSaveModal"
            :current-filters="currentFilters"
            :current-sort="currentSort"
            @save="onSave"
            @close="showSaveModal = false"
        />
    </div>
</template>

<style scoped>
.command-bar {
    margin-bottom: 1rem;
    position: relative;
}

.command-bar__row {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem 0.75rem;
    background: var(--bg-elevated);
    border: 1px solid var(--border-color);
    border-radius: var(--radius-lg);
    flex-wrap: wrap;
}

.command-bar__view-selector {
    position: relative;
    display: flex;
    align-items: center;
    gap: 0.375rem;
}

.command-bar__view-btn {
    display: flex;
    align-items: center;
    gap: 0.375rem;
    padding: 0.375rem 0.625rem;
    font-size: var(--text-sm);
    font-weight: 600;
    color: var(--text-primary);
    background: var(--bg-surface);
    border: 1px solid var(--border-color);
    border-radius: var(--radius-md);
    cursor: pointer;
    transition: all 0.15s;
}

.command-bar__view-btn:hover {
    border-color: var(--color-primary);
}

.command-bar__view-name {
    max-width: 200px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.command-bar__chevron {
    width: 14px;
    height: 14px;
    color: var(--text-muted);
}

.command-bar__modified-badge {
    font-size: var(--text-xs);
    font-weight: 600;
    color: var(--color-warning);
    background: rgba(245, 158, 11, 0.1);
    padding: 0.125rem 0.5rem;
    border-radius: var(--radius-full);
}

.command-bar__pills {
    display: flex;
    flex-wrap: wrap;
    gap: 0.375rem;
}

.command-bar__pill {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    padding: 0.25rem 0.5rem;
    font-size: var(--text-xs);
    color: var(--text-primary);
    background: rgba(18, 192, 221, 0.08);
    border: 1px solid rgba(18, 192, 221, 0.2);
    border-radius: var(--radius-full);
    cursor: pointer;
    transition: all 0.15s;
}

.command-bar__pill:hover {
    background: rgba(18, 192, 221, 0.15);
    border-color: var(--color-primary);
}

.command-bar__pill-remove {
    font-size: 0.75rem;
    color: var(--text-muted);
    margin-left: 0.125rem;
}

.command-bar__spacer {
    flex: 1;
}

.command-bar__toggle-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    padding: 0;
    border: 1px solid var(--border-color);
    border-radius: var(--radius-sm);
    background: var(--bg-surface);
    color: var(--text-muted);
    cursor: pointer;
    transition: all 0.15s;
}

.command-bar__toggle-btn:hover {
    color: var(--text-primary);
    border-color: var(--color-primary);
}

.command-bar__toggle-btn svg {
    width: 14px;
    height: 14px;
    transition: transform 0.2s;
}

.command-bar__toggle-btn--expanded svg {
    transform: rotate(180deg);
}

.command-bar__toggle-btn--expanded {
    color: var(--color-primary);
    border-color: var(--color-primary);
    background: rgba(18, 192, 221, 0.08);
}

.command-bar__actions {
    display: flex;
    align-items: center;
    gap: 0.5rem;
}

.command-bar__dropdown-area {
    position: relative;
}

.command-bar__action-btn {
    padding: 0.375rem 0.625rem;
    font-size: var(--text-xs);
    font-weight: 600;
    color: var(--color-primary);
    background: rgba(18, 192, 221, 0.08);
    border: 1px solid rgba(18, 192, 221, 0.15);
    border-radius: var(--radius-md);
    cursor: pointer;
    transition: all 0.15s;
}

.command-bar__action-btn:hover {
    background: rgba(18, 192, 221, 0.15);
    border-color: var(--color-primary);
}

.command-bar__count {
    font-size: var(--text-xs);
    color: var(--text-muted);
    padding: 0.375rem 0;
}

.command-bar__save-btn {
    padding: 0.375rem 0.75rem;
    font-size: var(--text-xs);
    font-weight: 600;
    color: #fff;
    background: var(--color-primary);
    border: none;
    border-radius: var(--radius-md);
    cursor: pointer;
    transition: opacity 0.15s;
}

.command-bar__save-btn:hover {
    opacity: 0.9;
}

.command-bar__save-btn--secondary {
    color: var(--color-primary);
    background: transparent;
    border: 1px solid var(--color-primary);
}

.command-bar__discard-btn {
    padding: 0.375rem 0.625rem;
    font-size: var(--text-xs);
    color: var(--text-muted);
    background: none;
    border: none;
    cursor: pointer;
}

.command-bar__discard-btn:hover {
    color: var(--text-primary);
}

.command-bar__export-btn {
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
}

.command-bar__export-btn:disabled {
    opacity: 0.7;
    cursor: not-allowed;
}

.command-bar__export-icon {
    width: 14px;
    height: 14px;
}

.command-bar__export-icon--spinning {
    animation: spin 1s linear infinite;
}

.command-bar__export-icon--spinning .spinner__track {
    opacity: 0.2;
}

.command-bar__export-icon--spinning .spinner__head {
    color: var(--color-primary);
}

@keyframes spin {
    to { transform: rotate(360deg); }
}

.command-bar__error {
    margin-top: 0.5rem;
    padding: 0.375rem 0.75rem;
    font-size: var(--text-xs);
    color: var(--color-danger);
    background: rgba(239, 68, 68, 0.08);
    border-radius: var(--radius-md);
}

/* Dropdowns positioned below their triggers */
.command-bar__dropdown-area > .view-switcher,
.command-bar__dropdown-area > .field-picker,
.command-bar__dropdown-area > .actions-menu {
    position: absolute;
    top: 100%;
    margin-top: 0.25rem;
    z-index: 50;
    max-width: min(400px, calc(100vw - 2rem));
}

/* View switcher opens left-aligned */
.command-bar__dropdown-area > .view-switcher {
    left: 0;
}

/* Filter picker opens right-aligned to stay on screen */
.command-bar__dropdown-area > .field-picker {
    right: 0;
}

/* Actions menu opens left-aligned within the view selector */
.command-bar__dropdown-area > .actions-menu {
    left: 0;
}

/* Transitions */
.dropdown-enter-active,
.dropdown-leave-active {
    transition: opacity 0.15s ease, transform 0.15s ease;
}

.dropdown-enter-from,
.dropdown-leave-to {
    opacity: 0;
    transform: translateY(-4px);
}

.slide-enter-active,
.slide-leave-active {
    transition: all 0.2s ease;
}

.slide-enter-from,
.slide-leave-to {
    opacity: 0;
    max-height: 0;
    overflow: hidden;
}

.slide-enter-to,
.slide-leave-from {
    max-height: 600px;
}
</style>
